import { type ReactNode, useCallback, useState } from 'react';

import { ClientIdentifyingInfoConfirmationDialog } from '@apps/web/components/settings/client_identifying_info_confirmation_dialog';
import { collectClientIdentifyingTemplateVariables } from '@apps/web/lib/client_identifying_template_variables';

type PendingConfirmation = {
    readonly resolve: (confirmed: boolean) => void;
    readonly identifyingVariables: ReadonlyArray<string>;
};

type ClientIdentifyingInfoConfirmationOptions = {
    readonly clientReferenceTerm: 'client' | 'patient';
    readonly confirmLabel?: string;
};

type ClientIdentifyingInfoConfirmation = {
    readonly confirmClientIdentifyingInfo: (
        templates: ReadonlyArray<string>,
    ) => Promise<boolean>;
    readonly clientIdentifyingInfoConfirmationDialog: ReactNode;
};

/** Request acknowledgement when SMS templates contain client identifiers. */
export function useClientIdentifyingInfoConfirmation({
    clientReferenceTerm,
    confirmLabel,
}: ClientIdentifyingInfoConfirmationOptions): ClientIdentifyingInfoConfirmation {
    const [pendingConfirmation, setPendingConfirmation] =
        useState<PendingConfirmation | null>(null);

    const confirmClientIdentifyingInfo = useCallback(
        (templates: ReadonlyArray<string>) => {
            const identifyingVariables =
                collectClientIdentifyingTemplateVariables(templates);
            if (identifyingVariables.length === 0) {
                return Promise.resolve(true);
            }
            return new Promise<boolean>((resolve) => {
                setPendingConfirmation({ resolve, identifyingVariables });
            });
        },
        [],
    );

    const finishConfirmation = (confirmed: boolean) => {
        pendingConfirmation?.resolve(confirmed);
        setPendingConfirmation(null);
    };

    const confirmationDialog = (
        <ClientIdentifyingInfoConfirmationDialog
            open={pendingConfirmation !== null}
            clientReferenceTerm={clientReferenceTerm}
            identifyingVariables={
                pendingConfirmation?.identifyingVariables ?? []
            }
            confirmLabel={confirmLabel}
            onOpenChange={(open) => {
                if (!open) {
                    finishConfirmation(false);
                }
            }}
            onConfirm={() => finishConfirmation(true)}
        />
    );

    return {
        confirmClientIdentifyingInfo,
        clientIdentifyingInfoConfirmationDialog: confirmationDialog,
    };
}
